import * as THREE from 'three'

import { FT_TO_M } from '../util/units.js'

/**
 * A ship's wake.
 *
 * The most conspicuous thing about a carrier at thirty knots, and the thing
 * this simulator was most obviously missing: an empty grey sea with a grey
 * ship on it, and nothing to say either of them was moving.
 *
 * It is also useful rather than merely pretty. On a real recovery the wake is
 * how you find the ship — you see the white line long before you can make out
 * the hull, and it points at the boat. From the downwind it is the clearest
 * indication of base recovery course you have.
 *
 * THE SHAPE IS NOT INVENTED
 * A displacement hull leaves a Kelvin wake: a turbulent churned trail directly
 * astern, and two divergent arms spreading from the bow at a half-angle of
 * about 19.5 degrees. That angle is a constant — it does not depend on the
 * ship's speed or size, which is one of the more surprising results in naval
 * hydrodynamics — so it is worth getting right rather than eyeballing, because
 * every photograph of a ship anyone has ever seen has it in.
 */

/** The Kelvin half-angle. Constant for any displacement hull at any speed. */
export const KELVIN_HALF_ANGLE_DEG = 19.47

/**
 * Paint the wake into a canvas: a bright churned core, two divergent arms and
 * enough noise that it does not read as a decal.
 *
 * Written straight into an ImageData rather than with canvas drawing calls,
 * because every pixel's alpha is a function of where it is — a per-pixel loop
 * is both simpler and faster than several hundred gradient fills.
 */
let cachedTexture = null

function wakeTexture(width = 256, height = 1024) {
  // One texture for the whole task group. It was being rebuilt per ship —
  // three identical megabyte canvases and two million calls to Math.sin on the
  // main thread before the first frame.
  if (cachedTexture) return cachedTexture

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const g = canvas.getContext('2d')
  const buffer = new Uint8ClampedArray(width * height * 4)

  for (let y = 0; y < height; y++) {
    // v = 0 at the stern, 1 far astern.
    const v = y / height
    // Turbulence dies away fast: most of the visible churn is within a few
    // ship lengths of the stern, and after that it is just a pale streak.
    const decay = Math.pow(1 - v, 1.35)
    // The core fills more of the texture astern, because the QUAD is already
    // opening at the Kelvin angle underneath it — u = 0 and u = 1 are the
    // divergent arms by construction. Drawing arms into the texture as well,
    // which is what this did first, double-counts the divergence: they came
    // out as a parabola starting at 2.8 degrees and running off the edge of
    // the texture two thirds of the way down.
    const coreHalf = (0.20 + v * 0.40) * width
    // The arms live at the very edges of the quad, so in texture space they
    // are at u = 0 and u = 1 and stay there.
    const armHalf = 0.055 * width

    for (let x = 0; x < width; x++) {
      const dx = x - width / 2

      const core = Math.max(0, 1 - Math.abs(dx) / coreHalf) ** 1.6
      const edge = width / 2 - Math.abs(dx)
      const arm = Math.max(0, 1 - edge / armHalf) ** 2

      // Streaky, flow-aligned noise. Three sines beating against each other is
      // enough structure at this size and costs nothing.
      const n =
        0.5 +
        0.5 *
          Math.sin(x * 0.31 + y * 0.09) *
          Math.sin(x * 0.07 - y * 0.23) *
          Math.sin(y * 0.017 + x * 0.011)

      const alpha = decay * (core * (0.55 + 0.45 * n) + arm * 0.55)

      // WHITE EVERYWHERE, transparent where there is no foam. Leaving the
      // empty texels at RGB zero looks identical in a paint program and is not
      // identical to a GPU: mipmapping and bilinear filtering average the
      // colour channels as well as alpha, so black bleeds into white and the
      // wake goes progressively grey with distance — which is exactly where it
      // is supposed to be the brightest thing on the sea.
      const i = (y * width + x) * 4
      buffer[i] = 255
      buffer[i + 1] = 255
      buffer[i + 2] = 255
      buffer[i + 3] = Math.min(255, alpha * 255)
    }
  }

  g.putImageData(new ImageData(buffer, width, height), 0, 0)

  const tex = new THREE.CanvasTexture(canvas)
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.colorSpace = THREE.SRGBColorSpace
  tex.needsUpdate = true
  cachedTexture = tex
  return tex
}

export class Wake {
  /**
   * @param {object} o
   * @param {number} o.lengthFt   how far astern the wake runs
   * @param {number} o.beamFt     the ship's beam, which sets the initial width
   * @param {number} [o.opacity]
   * @param {number} [o.sternFt]  where the stern is, in the ship's own frame
   */
  constructor({ lengthFt, beamFt, opacity = 0.55, sternFt = 0 }) {
    this.group = new THREE.Group()
    this.group.name = 'Wake'

    this.texture = wakeTexture()

    this.material = new THREE.MeshBasicMaterial({
      map: this.texture,
      transparent: true,
      opacity,
      depthWrite: false,
      side: THREE.DoubleSide,
      fog: true,
    })

    // The quad widens astern by the Kelvin angle. Built as a trapezoid rather
    // than scaled, so the texture is not stretched differently at the two ends.
    const nearHalf = beamFt * 1.15 * FT_TO_M
    const spread = Math.tan((KELVIN_HALF_ANGLE_DEG * Math.PI) / 180)
    const len = lengthFt * FT_TO_M
    const farHalf = nearHalf + spread * len

    const geo = new THREE.BufferGeometry()
    geo.setAttribute(
      'position',
      new THREE.Float32BufferAttribute(
        [-nearHalf, 0, 0, nearHalf, 0, 0, -farHalf, 0, len, farHalf, 0, len],
        3,
      ),
    )
    geo.setAttribute('uv', new THREE.Float32BufferAttribute([0, 1, 1, 1, 0, 0, 1, 0], 2))
    geo.setIndex([0, 2, 1, 1, 2, 3])
    geo.computeVertexNormals()

    this.mesh = new THREE.Mesh(geo, this.material)
    // A whisker above the water. Any lower and the sea's own waves poke
    // through it; any higher and it visibly floats.
    this.mesh.position.set(0, 0.35, sternFt * FT_TO_M)
    this.mesh.renderOrder = 2
    this.mesh.frustumCulled = false
    this.group.add(this.mesh)
  }

  dispose() {
    this.mesh.geometry.dispose()
    this.material.dispose()
    // The texture is shared across every wake in the scene, so it is not this
    // instance's to dispose.
  }
}

/**
 * The white water thrown up either side of the bow.
 *
 * Separate from the wake because it behaves differently: it is a standing
 * feature of the hull pushing water aside, brightest right at the stem, and it
 * does not trail.
 */
export function bowWave({ beamFt, lengthFt = 320, opacity = 0.5 }) {
  const c = document.createElement('canvas')
  c.width = 128
  c.height = 128
  const g = c.getContext('2d')
  // Centred, not offset. With a radial gradient painted a third of the way up
  // the canvas the bright core landed 67 ft AHEAD of the ship's stem — a
  // detached white ellipse in open sea, leading the bow like a bow wave that
  // had got away.
  const grad = g.createRadialGradient(64, 64, 4, 64, 64, 78)
  grad.addColorStop(0, 'rgba(255,255,255,0.95)')
  grad.addColorStop(0.35, 'rgba(255,255,255,0.42)')
  grad.addColorStop(1, 'rgba(255,255,255,0)')
  g.fillStyle = grad
  g.fillRect(0, 0, 128, 128)

  const tex = new THREE.CanvasTexture(c)
  tex.colorSpace = THREE.SRGBColorSpace
  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(beamFt * 2.6 * FT_TO_M, lengthFt * FT_TO_M),
    new THREE.MeshBasicMaterial({
      map: tex,
      transparent: true,
      opacity,
      depthWrite: false,
    }),
  )
  mesh.rotation.x = -Math.PI / 2
  mesh.renderOrder = 2
  return mesh
}
